import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/api-base-url';
import type { PresignAvatarInput, UpdateProfileInput } from '../schemas';

export interface ProfileView {
  id: string;
  email: string;
  status: 'active' | 'suspended' | 'soft_deleted';
  fullName: string | null;
  phone: string | null;
  timeZone: string | null;
  avatarUrl: string | null;
  roles: string[];
  impersonatedBy: string | null;
}

export interface AvatarPresignView {
  uploadUrl: string;
  key: string;
}

export class ProfileApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ProfileApiError';
    this.status = status;
  }
}

const PROFILE_QUERY_KEY: readonly unknown[] = ['profile', 'me'];

/** Retry cadence while the backend does not answer at the network level. */
const UNREACHABLE_RETRY_MS = 3_000;

/**
 * True when the profile request failed without an HTTP response — connection
 * reset/refused while the backend boots, DNS failure, etc. `fetch` rejects
 * with a `TypeError` in those cases; HTTP failures become `ProfileApiError`.
 * Anything that is not a `ProfileApiError` must NOT be treated as
 * "signed out": redirecting to /sign-in on a network error causes a
 * navigation loop (the token is still present, so /sign-in bounces back).
 */
export function isBackendUnreachableError(error: unknown): boolean {
  return error != null && !(error instanceof ProfileApiError);
}

async function readError(response: Response): Promise<ProfileApiError> {
  let message = `Request failed (${String(response.status)})`;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // ignore
  }
  return new ProfileApiError(response.status, message);
}

async function fetchMe(): Promise<ProfileView> {
  const response = await authFetch(`${getApiBaseUrl()}/me`, { method: 'GET' });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as ProfileView;
}

async function patchMe(body: UpdateProfileInput): Promise<ProfileView> {
  const response = await authFetch(`${getApiBaseUrl()}/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as ProfileView;
}

async function requestAvatarPresign(input: PresignAvatarInput): Promise<AvatarPresignView> {
  const response = await authFetch(`${getApiBaseUrl()}/me/avatar/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as AvatarPresignView;
}

export async function uploadAvatar(file: File): Promise<string> {
  const presign = await requestAvatarPresign({
    contentType: file.type as PresignAvatarInput['contentType'],
    contentLength: file.size,
  });
  const putResponse = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!putResponse.ok) {
    throw new ProfileApiError(putResponse.status, 'Avatar upload failed.');
  }
  return presign.key;
}

export function useProfileQuery() {
  return useQuery<ProfileView, ProfileApiError>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchMe,
    // Network-level failures retry forever inside ONE retryer: the query stays
    // `pending` (with `failureReason` set), so gates render a stable retry
    // screen. Letting it reach the error state instead causes a mount/unmount
    // loop: every remount refetches, and a refetching no-data query flips back
    // to `pending`, which collapses the gate again. HTTP errors (401, …)
    // surface immediately.
    retry: (_failureCount, error) => isBackendUnreachableError(error),
    retryDelay: UNREACHABLE_RETRY_MS,
    staleTime: 30_000,
  });
}

export function useUpdateProfileMutation() {
  const client = useQueryClient();
  return useMutation<ProfileView, ProfileApiError, UpdateProfileInput>({
    mutationFn: patchMe,
    onSuccess: (data) => {
      client.setQueryData(PROFILE_QUERY_KEY, data);
    },
  });
}
